import { useState } from "react";
import { Btn, Card, Checkbox, Icon, Input, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { Screen, PageHeader } from "../../shell/chrome";
import { StatTile, FilterBar, FilterDropdown, ExplainerFooter, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, useRowSelection, BulkActionBar, CardActionBar, CARD_GRID, tabExport, eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "../events/eventSectionCounts";
import { EventsSectionState } from "../events/EventsSectionState";
import { thStyle, tdStyle } from "../events/EventCard";
import { CreationStore } from "../events/creation/store";
import { toast } from "../../shell/toast";
import { useTemplates, reflectTemplateDto, removeTemplateLocal } from "./useTemplates";
import { duplicateEventTemplate, deleteEventTemplate } from "@v0y4ge/api-client";
import type { EventTemplate } from "./templates-data";
import type { NavApi } from "../../shell/router";
import { useT, t as i18nT } from "../../lib/i18n";

/** Open the wizard pre-filled from a template. Templates saved from an event
 *  carry a full-definition snapshot (`config`) — apply it so the new event is
 *  as complete as the source (minus dates + registration window). */
function useTemplate(t: EventTemplate, nav: NavApi) {
  if (t.config && Object.keys(t.config).length) CreationStore.applyTemplate(t);
  else CreationStore.setType(t.typeId || t.id);
  nav.go("create.basics", { templateId: t.id });
}

export default function EventTemplates() {
  const t = useT();
  const translate = t;
  const nav = useNav();
  const { templates, loading } = useTemplates();
  const [view, setView] = useViewMode("templates", "list");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");

  const types = [...new Set(templates.map((t) => t.type))];
  const filtered = templates.filter((t) => {
    if (typeFilter && t.type !== typeFilter) return false;
    if (statusFilter !== "all" && (t.status || "published") !== statusFilter) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      const hay = `${t.name} ${t.type} ${(t.tags || []).join(" ")} ${t.desc || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const sel = useRowSelection(filtered.map((t) => t.id));
  const publishedCount = templates.filter((t) => (t.status || "published") === "published").length;
  const draftCount = templates.length - publishedCount;
  const anyFilter = typeFilter || statusFilter !== "all" || query.trim();
  const totalUsage = templates.reduce((s, t) => s + t.usage, 0);

  const duplicateTemplate = async (t: EventTemplate) => {
    try {
      const dto = await duplicateEventTemplate(t.id);
      const nt = reflectTemplateDto(dto as unknown as Parameters<typeof reflectTemplateDto>[0]);
      toast({ tone: "success", title: translate("templates.eventTemplates.templateDuplicated", "Template duplicated"), subtitle: i18nT("templates.eventTemplates.aCopyOfTNameWasAddedAsADraft.icu", { value0: t.name }) });
      return nt;
    } catch (e) { console.warn("duplicateEventTemplate failed:", e); toast({ tone: "danger", title: translate("templates.eventTemplates.couldnTDuplicateTemplate", "Couldn't duplicate template"), subtitle: translate("templates.eventTemplates.noCopyWasCreatedRetryInAMoment", "No copy was created. Retry in a moment.") }); }
  };
  const removeTemplate = async (t: EventTemplate) => {
    try { await deleteEventTemplate(t.id); removeTemplateLocal(t.id); toast({ tone: "warning", title: translate("templates.eventTemplates.templateDeleted", "Template deleted"), subtitle: i18nT("templates.eventTemplates.tNameWasRemoved.icu", { value0: t.name }) }); }
    catch (e) { console.warn("deleteEventTemplate failed:", e); toast({ tone: "danger", title: translate("templates.eventTemplates.couldnTDeleteTemplate", "Couldn't delete template"), subtitle: translate("templates.eventTemplates.theTemplateIsUnchangedRetryInAMoment", "The template is unchanged. Retry in a moment.") }); }
  };
  const tmplActions = (t: EventTemplate) => ({
    onView: () => nav.go("templates.detail", { id: t.id }),
    viewLabel: "Preview",
    onEdit: () => { CreationStore.loadTemplateForEdit(t); nav.go("create.basics"); },
    editLabel: "Edit",
    onDuplicate: () => void duplicateTemplate(t),
    moreHeader: t.name,
    audit: { type: "template" as const, id: t.id, name: t.name },
    moreItems: [
      { icon: <Icon.plus size={14} />, label: translate("templates.eventTemplates.useThisTemplate", "Use this template"), sub: "Start a new event", onClick: () => useTemplate(t, nav) },
      { icon: <Icon.download size={14} />, label: translate("common.actions.export", "Export"), onClick: () => tabExport("templates", [t] as unknown as Record<string, unknown>[]) },
      "divider" as const,
      { icon: <Icon.trash size={14} />, label: translate("templates.eventTemplates.deleteTemplate", "Delete template"), danger: true, onClick: () => void removeTemplate(t) },
    ],
  });

  return (
    <Screen rail="events" tabs={eventsSectionTabs(nav, "templates", useEventSectionCounts())} activeTab="templates">
      <PageHeader
        title={t("templates.eventTemplates.eventTemplates", "Event Templates")}
        subtitle={t("templates.eventTemplates.templatesLengthTemplateTemplatesLength1STotalusageEventsCreatedFromTempl.icu", { count: templates.length, totalUsage })}
        breadcrumb={null}
        actions={<>
          <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => tabExport("templates", filtered as unknown as Record<string, unknown>[])}>{t("common.actions.export", "Export")}</Btn>
          <Btn icon={<Icon.plus size={14} />} onClick={() => { CreationStore.startTemplate(); nav.go("create.type"); }}>{t("templates.eventTemplates.newTemplate", "New template")}</Btn>
        </>}
      />

      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        <StatTile label={t("templates.eventTemplates.templates", "Templates")} value={templates.length} icon={<Icon.layers size={14} />} accent={T.p1} />
        <StatTile label={t("templates.eventTemplates.eventsFromTemplates", "Events from templates")} value={totalUsage} icon={<Icon.calendar size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("templates.eventTemplates.eventTypes", "Event types")} value={types.length} icon={<Icon.tags size={14} />} accent={T.green1} />
      </div>

      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("templates.eventTemplates.searchTemplates", "Search templates…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 280 }} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        {[
          { id: "all", label: i18nT("templates.eventTemplates.allTemplatesLength.icu", { value0: templates.length }) },
          { id: "published", label: i18nT("templates.eventTemplates.publishedPublishedcount.icu", { value0: publishedCount }) },
          { id: "draft", label: i18nT("templates.eventTemplates.draftsDraftcount.icu", { value0: draftCount }) },
        ].map((t) => (
          <button key={t.id} onClick={() => setStatusFilter(t.id)} style={{ padding: "6px 12px", borderRadius: 999, fontSize: 13, fontWeight: 500, background: statusFilter === t.id ? T.p1 : T.white, color: statusFilter === t.id ? T.white : T.n1, border: `1px solid ${statusFilter === t.id ? T.p1 : T.borderStrong}`, cursor: "pointer", fontFamily: T.font }}>{t.label}</button>
        ))}
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <FilterDropdown label={typeFilter || t("templates.eventTemplates.anyType", "Any type")} active={!!typeFilter} options={types} onPick={setTypeFilter} onClear={() => setTypeFilter(null)} />
        {anyFilter ? <button onClick={() => { setTypeFilter(null); setStatusFilter("all"); setQuery(""); }} style={{ padding: "6px 10px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: "transparent", color: T.p1, border: "none", cursor: "pointer", fontFamily: T.font }}>{t("templates.eventTemplates.clearAll", "Clear all")}</button> : null}
        <span style={{ marginLeft: "auto" }} />
        <ViewToggle mode={view} onChange={setView} />
      </FilterBar>

      <BulkActionBar count={sel.selected.size} noun="template" onClear={sel.clear} actions={[
        { label: t("templates.eventTemplates.duplicate", "Duplicate"), icon: <Icon.copy size={12} />, onClick: () => { toast({ tone: "success", title: t("templates.eventTemplates.duplicatedSelSelectedSizeTemplateSelSelectedSize1S.icu", { count: sel.selected.size }) }); sel.clear(); } },
        { label: t("common.actions.export", "Export"), icon: <Icon.download size={12} />, onClick: () => { tabExport("templates", filtered.filter((x) => sel.selected.has(x.id)) as unknown as Record<string, unknown>[]); sel.clear(); } },
        { label: t("common.actions.delete", "Delete"), icon: <Icon.trash size={12} />, danger: true, onClick: () => { toast({ tone: "warn", title: t("templates.eventTemplates.deletedSelSelectedSizeTemplateSelSelectedSize1S.icu", { count: sel.selected.size }) }); sel.clear(); } },
      ]} />

      <div style={{ padding: "0 24px" }}>
        <ExplainerFooter>
          <b>{t("templates.eventTemplates.eventTemplates", "Event Templates")}</b> {t("templates.eventTemplates.areReusableBlueprintsTheyPresetTheBookingFlowTicketsCommsAndCheck", "are reusable blueprints — they preset the booking flow, tickets, comms and check-in for a kind of event. Use one to start a new event with everything pre-filled, then customise per event.")}</ExplainerFooter>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {loading || filtered.length === 0 ? (
          <EventsSectionState noun={t("events.sectionState.nounTemplates", "templates")} icon={<Icon.layers size={20} />}
            loading={loading} empty={filtered.length === 0} filtered={!!anyFilter}
            onClearFilters={() => { setTypeFilter(null); setStatusFilter("all"); setQuery(""); }}
            emptyBody={t("templates.eventTemplates.createAReusableBlueprintToSpinUpEventsFaster", "Create a reusable blueprint to spin up events faster.")}
            action={<Btn icon={<Icon.plus size={14} />} onClick={() => { CreationStore.startTemplate(); nav.go("create.type"); }}>{t("templates.eventTemplates.newTemplate", "New template")}</Btn>} />
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16 }}>
            {filtered.map((t) => (
              <Card key={t.id} onClick={() => nav.go("templates.detail", { id: t.id })} style={{ padding: 0, cursor: "pointer", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ height: 96, background: `linear-gradient(135deg, ${t.hue}, ${T.p1})`, position: "relative", padding: 14, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Pill tone="primary" style={{ background: "rgba(255,255,255,0.25)", color: T.white }}>{t.type}</Pill>
                  </div>
                  <div style={{ color: T.white, fontWeight: 700, fontSize: 18, lineHeight: 1.2, letterSpacing: -0.3 }}>{t.name}</div>
                </div>
                <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
                  <p style={{ fontSize: 13, color: T.soft, margin: 0, lineHeight: 1.5 }}>{t.desc}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{t.tags.map((tag) => <Pill key={tag}>{tag}</Pill>)}</div>
                  <div style={{ display: "flex", gap: 16, fontSize: 11, color: T.soft, marginTop: 4 }}>
                    <span><Icon.users size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{t.defaults.capacity} {translate("templates.eventTemplates.cap", "cap")}</span>
                    <span><Icon.ticket size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{t.defaults.tickets} {t.defaults.tickets === 1 ? translate('templates.eventTemplates.text') : translate('templates.eventTemplates.text2')}</span>
                    <span><Icon.mail size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{t.defaults.comms} {translate("templates.eventTemplates.comms", "comms")}</span>
                  </div>
                </div>
                <CardActionBar left={<span>{translate("templates.eventTemplates.used", "Used")}<strong style={{ color: T.n1 }}>{t.usage}×</strong> {translate("templates.eventTemplates.edited0d79ff16", "· edited")}{t.lastEdit}</span>} {...tmplActions(t)} />
              </Card>
            ))}
          </div>
        ) : (
          <Card style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                  <th style={{ ...thStyle, width: 36, paddingRight: 0, boxSizing: "content-box" }}><Checkbox checked={sel.allChecked} indeterminate={sel.someChecked} onChange={sel.toggleAll} /></th>
                  <th style={thStyle}>{t("templates.eventTemplates.template", "Template")}</th>
                  <th style={thStyle}>{t("templates.eventTemplates.type", "Type")}</th>
                  <th style={thStyle}>{t("templates.eventTemplates.used", "Used")}</th>
                  <th style={thStyle}>{t("templates.eventTemplates.edited", "Edited")}</th>
                  <th style={{ ...thStyle, width: 140, textAlign: "right", boxSizing: "content-box" }}>{t("templates.eventTemplates.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t, i) => (
                  <tr key={t.id} onClick={() => nav.go("templates.detail", { id: t.id })} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", background: sel.selected.has(t.id) ? T.p4 : T.white }}>
                    <td style={{ ...tdStyle, paddingRight: 0 }} onClick={(e) => { e.stopPropagation(); sel.toggle(t.id); }}><Checkbox checked={sel.selected.has(t.id)} /></td>
                    <td style={tdStyle}>
                      <div>
                        <div style={{ color: T.n1, fontWeight: 600 }}>{t.name}</div>
                        <div style={{ color: T.soft, fontSize: 11 }}>{t.tags.join(" · ")}</div>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, color: T.n1 }}>{t.type}</td>
                    <td style={{ ...tdStyle, color: T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{t.usage}×</td>
                    <td style={{ ...tdStyle, color: T.soft, fontSize: 12 }}>{t.lastEdit}</td>
                    <td style={tdStyle} onClick={(e) => e.stopPropagation()}><RowActions {...tmplActions(t)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </Screen>
  );
}
